import { useEffect, useState, type FormEvent } from "react";

import { useCreateRule, useUpdateRule, type AlertRule, type RuleType, type Severity } from "@/api/alerts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AddRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set to edit an existing rule; null = create flow. */
  rule?: AlertRule | null;
}

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  camera_offline: "Camera Offline",
  camera_degraded: "Camera Degraded",
  recording_gap: "Recording Gap",
  recording_stopped: "Recording Stopped",
  health_threshold: "Health Threshold",
  license_expiring: "License Expiring",
  agent_unreachable: "SLink Agent Unreachable",
};

// Exactly engine_v2.SUPPORTED_RULE_TYPES. Originally dropped camera_degraded and
// site_unhealthy: the API accepted them (201) but v2 had no evaluator, so they
// were stored, enabled, and silently never fired.
//
// SS15 restored camera_degraded — it now HAS an evaluator. site_unhealthy stays
// dropped and was deliberately not ported (backlog §15a).
const SUPPORTED_TYPES: RuleType[] = [
  "camera_offline",
  // SS15 — ported to engine_v2, so it fires now. No parameter form: it defaults
  // to statuses ["error"], which is v1's exact behaviour. The `statuses`
  // override is API-only for now (backlog §15a).
  "camera_degraded",
  "recording_gap",
  "recording_stopped",
  "health_threshold",
  "license_expiring",
  "agent_unreachable",
];

export function AddAlertRuleDialog({
  open,
  onOpenChange,
  rule,
}: AddRuleDialogProps) {
  const isEdit = Boolean(rule);
  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState<RuleType>("camera_offline");
  const [severity, setSeverity] = useState<Severity>("warning");
  const [offlineMinutes, setOfflineMinutes] = useState<number>(5);
  const [healthPctBelow, setHealthPctBelow] = useState<number>(50);
  const [daysBefore, setDaysBefore] = useState<number>(7);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [cooldown, setCooldown] = useState<number>(30);
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateRule();
  const update = useUpdateRule(rule?.id ?? "");

  useEffect(() => {
    if (!open) return;
    if (rule) {
      setName(rule.name);
      setRuleType(rule.rule_type);
      setSeverity(rule.severity);
      setOfflineMinutes(
        Number((rule.condition_params as any)?.offline_minutes ?? 5),
      );
      setHealthPctBelow(
        Number((rule.condition_params as any)?.health_pct_below ?? 50),
      );
      setDaysBefore(
        Number((rule.condition_params as any)?.days_before ?? 7),
      );
      setNotifyEmail(rule.notify_email);
      setWebhookUrl(rule.webhook_url ?? "");
      setCooldown(rule.cooldown_minutes);
      setEnabled(rule.enabled);
    } else {
      setName("");
      setRuleType("camera_offline");
      setSeverity("warning");
      setOfflineMinutes(5);
      setHealthPctBelow(50);
      setDaysBefore(7);
      setNotifyEmail(true);
      setWebhookUrl("");
      setCooldown(30);
      setEnabled(true);
    }
    setError(null);
  }, [open, rule]);

  const conditionParams: Record<string, unknown> = (() => {
    if (ruleType === "camera_offline") {
      return { offline_minutes: offlineMinutes };
    }
    if (ruleType === "health_threshold") {
      // v2's _eval_health_threshold reads rule.conditions {metric, below} —
      // NOT v1's condition_params.health_pct_below.
      return { metric: "uptime_pct", below: healthPctBelow };
    }
    if (ruleType === "license_expiring") {
      return { days_before: daysBefore };
    }
    return {};
  })();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const payload = {
      name,
      rule_type: ruleType,
      condition_params: conditionParams,
      severity,
      notify_email: notifyEmail,
      notify_webhook: Boolean(webhookUrl),
      webhook_url: webhookUrl || null,
      cooldown_minutes: cooldown,
      enabled,
    };
    try {
      if (isEdit) {
        await update.mutateAsync(payload);
      } else {
        await create.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Could not save rule";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit alert rule" : "Add alert rule"}</DialogTitle>
          <DialogDescription>
            Rules are evaluated every {`{interval}`} seconds. Cooldown prevents
            duplicate alerts for the same camera/site within the window.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label htmlFor="rule_name">Name</Label>
            <Input
              id="rule_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="rule_type">Rule type</Label>
              <select
                id="rule_type"
                value={ruleType}
                onChange={(e) => setRuleType(e.target.value as RuleType)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {SUPPORTED_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {RULE_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule_sev">Severity</Label>
              <select
                id="rule_sev"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as Severity)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>
          {ruleType === "camera_offline" && (
            <div className="space-y-2">
              <Label htmlFor="rule_off">Offline threshold (minutes)</Label>
              <Input
                id="rule_off"
                type="number"
                min={1}
                value={offlineMinutes}
                onChange={(e) => setOfflineMinutes(Number(e.target.value))}
              />
            </div>
          )}
          {ruleType === "health_threshold" && (
            <div className="space-y-2">
              <Label htmlFor="rule_health">Trigger when health % falls below</Label>
              <Input
                id="rule_health"
                type="number"
                min={0}
                max={100}
                value={healthPctBelow}
                onChange={(e) => setHealthPctBelow(Number(e.target.value))}
              />
            </div>
          )}
          {ruleType === "license_expiring" && (
            <div className="space-y-2">
              <Label htmlFor="rule_days">Days before expiry</Label>
              <Input
                id="rule_days"
                type="number"
                min={1}
                value={daysBefore}
                onChange={(e) => setDaysBefore(Number(e.target.value))}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="rule_cd">Cooldown (minutes)</Label>
              <Input
                id="rule_cd"
                type="number"
                min={0}
                value={cooldown}
                onChange={(e) => setCooldown(Number(e.target.value))}
              />
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)}
                />
                Notify by email
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Enabled
              </label>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="rule_hook">Webhook URL (optional)</Label>
            <Input
              id="rule_hook"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://hooks.example/incoming"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending
                ? "Saving…"
                : isEdit
                  ? "Save changes"
                  : "Create rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
